import { TableRow, styled } from "@mui/material";

import { basicColours } from "../theme/colours";

export const INELIGIBLE_UNSELECTED_TEXT_COLOR = basicColours.mono[400];
export const INELIGIBLE_SELECTED_TEXT_COLOR = basicColours.mono[500];

const ASTableV2RowStyled = styled(TableRow, {
  shouldForwardProp: (propName) =>
    ![
      "expanded",
      "selected",
      "highlighted",
      "disabled",
      "warning",
      "sanitize",
      "ineligible"
    ].includes(propName as string)
})<{
  selected?: boolean;
  highlighted?: boolean;
  expanded?: boolean;
  disabled?: boolean;
  warning?: boolean;
  sanitize?: boolean;
  ineligible?: boolean;
}>(({
  theme: { palette, spacing },
  selected,
  highlighted,
  expanded,
  disabled,
  warning,
  sanitize,
  ineligible,
  onClick
}) => {
  const getBorderColor = () => {
    switch (true) {
      case selected && ineligible:
        return palette.error.dark;
      case selected && sanitize:
        return palette.infoPurple.dark;
      case selected && warning:
        return palette.warning.dark;
      case selected:
        return palette.tableRowSelected.main;
      default:
        return "transparent";
    }
  };

  const getBorderBottomColor = () => {
    switch (true) {
      case selected && ineligible && !expanded:
        return palette.error.dark;
      case selected && sanitize && !expanded:
        return palette.infoPurple.dark;
      case selected && warning && !expanded:
        return palette.warning.dark;
      case selected && !expanded:
        return palette.tableRowSelected.main;
      default:
        return "transparent";
    }
  };

  const getBackgroundColor = () => {
    switch (true) {
      case selected && ineligible:
        return palette.error.light;
      case selected && sanitize:
        return palette.infoPurple.main;
      case selected && warning:
        return palette.warning.light;
      case highlighted:
        return palette.primary.light;
      case selected:
        return palette.tableRowSelected.light;
      default:
        return palette.background.default;
    }
  };

  const getTextColor = () => {
    switch (true) {
      case ineligible && selected:
        return INELIGIBLE_SELECTED_TEXT_COLOR;
      case ineligible && !selected:
        return INELIGIBLE_UNSELECTED_TEXT_COLOR;
      default:
        return palette.text.primary;
    }
  };

  const borderColor = getBorderColor();
  const borderBottomColor = getBorderBottomColor();
  const backgroundColor = getBackgroundColor();

  return {
    display: "contents",
    "& > td:first-of-type": {
      borderTopLeftRadius: spacing(1),
      borderBottomLeftRadius: expanded ? spacing(0) : spacing(1),
      borderLeft: "solid 3px",
      borderColor,
      borderBottomColor
    },
    "& > td:last-of-type": {
      borderTopRightRadius: spacing(1),
      borderBottomRightRadius: expanded ? spacing(0) : spacing(1),
      borderRight: "solid 3px",
      borderColor,
      borderBottomColor
    },
    "& > td": {
      cursor: onClick && !disabled ? "pointer" : "default",
      opacity: disabled ? 0.5 : 1,
      border: 0,
      borderTop: "solid 3px",
      borderBottom: "solid 3px",
      borderColor,
      borderBottomColor,
      marginTop: spacing(1),
      padding: spacing(2),
      backgroundColor,
      color: getTextColor()
    }
  };
});

type Props = Parameters<typeof ASTableV2RowStyled>[0] & {
  disabled?: boolean;
  warning?: boolean;
  ineligible?: boolean;
  role?: React.AriaRole;
};

export const ASTableV2Row = (props: Props) => {
  return (
    <ASTableV2RowStyled
      {...props}
      aria-selected={props.selected}
      disabled={props.disabled}
    />
  );
};
